import React, { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography, Chip } from '@mui/material';
import { LocalHospital as DocIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/environment';

export interface ProfessionalOption {
  id: string;
  full_name: string;
  specialty?: string;
  registration_number?: string;
  commission_percentage?: number;
}

interface ProfessionalAutocompleteProps {
  value: ProfessionalOption | null;
  onChange: (professional: ProfessionalOption | null) => void;
  label?: string;
  size?: 'small' | 'medium';
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

const ProfessionalAutocomplete: React.FC<ProfessionalAutocompleteProps> = ({
  value,
  onChange,
  label = 'Profissional Responsável',
  size = 'small',
  required = false,
  disabled = false,
  error = false,
  helperText,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<ProfessionalOption[]>([]);
  const [loading, setLoading] = useState(false);

  const searchProfessionals = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl('/clinic/professionals'), {
        params: { search: query, limit: 15, is_active: true },
      });
      const raw = (res.data?.data || res.data || []).filter((p: any) => p?.is_active !== false);
      setOptions(
        raw.map((p: any) => ({
          id: p.id,
          full_name: p.full_name,
          specialty: p.specialty || '',
          registration_number: p.registration_number || '',
          commission_percentage: p.commission_percentage ?? 0,
        })),
      );
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchProfessionals(inputValue), 300);
    return () => clearTimeout(timer);
  }, [inputValue, searchProfessionals]);

  return (
    <Autocomplete
      value={value}
      onChange={(_, newVal) => onChange(newVal)}
      inputValue={inputValue}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      options={options}
      loading={loading}
      disabled={disabled}
      getOptionLabel={(opt) => opt.full_name || ''}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      noOptionsText={inputValue.length < 2 ? 'Digite ao menos 2 caracteres' : 'Nenhum profissional encontrado'}
      loadingText="Buscando..."
      filterOptions={(x) => x}
      renderOption={(props, opt) => (
        <Box component="li" {...props} key={opt.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
          <DocIcon sx={{ color: '#10b981', fontSize: 20, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{opt.full_name}</Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {opt.specialty && <span>{opt.specialty}</span>}
              {opt.registration_number && <span>CRM: {opt.registration_number}</span>}
              {(opt.commission_percentage ?? 0) > 0 && (
                <Chip label={`${opt.commission_percentage}%`} size="small" sx={{ height: 16, fontSize: '0.65rem', bgcolor: '#dcfce7', color: '#166534' }} />
              )}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size={size}
          required={required}
          error={error}
          helperText={helperText}
          placeholder="Buscar por nome, CRM ou especialidade..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ProfessionalAutocomplete;
